import {
  Dialog,
  DialogPanel,
  DialogTitle,
  DialogBackdrop,
} from '@headlessui/react';
import { Edge, Node, useReactFlow } from '@xyflow/react';
import { useState, Fragment, useEffect } from 'react';
import { Button } from 'src/components/button';
import FormInput from 'src/components/form-input';
import { CloseIon } from 'src/components/icons/CloseIcon';
import IPInput from 'src/components/ip-input';
import LoadingSpinner from 'src/components/loading/spinner';
import { useGlobalContext } from 'src/context/global/use-global-context';
import { useUndoRedo } from 'src/context/undo-redo';
import { dispatchNodesChanged } from 'src/utils/dispatch-nodes-changed';
import { countLeading255Octets, calculateNetworkAddress2 } from 'src/utils/ip';
import {
  orderNodes,
  groupBySwitch,
  filterCanSettingNode,
  sortGroupsBySwitchName,
  sortDevicesWithinGroups,
} from 'src/utils/orderNode';
import {
  validateNode,
  allElementsValid,
  allElementsAreEqual,
} from 'src/utils/validate';
import { twMerge } from 'tailwind-merge';

import {
  EDataType,
  EDataSwitchType,
  NodeCategoryModel,
} from '../models/node-data';
import { NodeModel } from '../models/node-model';

export default function DialogSetting() {
  const [isLoading, setIsLoading] = useState(true);
  const { showDialogs, setShowDialogs, useCustomVlan, globalIpSubnet } =
    useGlobalContext();
  const { takeSnapshot } = useUndoRedo();
  const { getNodes, setNodes, getEdges } = useReactFlow();
  const nodes = getNodes();
  const edges = getEdges();

  const [currentNodes, setCurrentNodes] = useState<Node[]>([]);
  const [groupColor, setGroupColor] = useState<Record<string, string>>({});

  const [globalError, setGlobalError] = useState<string>('');

  const onCancel = () => {
    setShowDialogs({ setting: false });
  };

  const hasGlobalIpSubnet = Boolean(
    !useCustomVlan && globalIpSubnet?.subnetMask,
  );

  const onChangeData = (nodeId: string, key: string, value: string) => {
    setCurrentNodes((old) => {
      const newNodes = [...old];
      const index = newNodes.findIndex((n) => n.id === nodeId);
      if (index >= 0) {
        const category = newNodes[index].data.category as NodeCategoryModel;
        const categoryUpdate: Partial<NodeCategoryModel> = { [key]: value };
        const noIpInterfaces =
          !category.ipInterfaces || category.ipInterfaces.length === 0;

        let nextSubnetMask = category.subnetMask ?? '';

        if (key === 'ip' || key === 'subnetMask') {
          if (noIpInterfaces) {
            const ip = key === 'ip' ? value : category.ip || '';
            const mask =
              key === 'subnetMask' ? value : category.subnetMask || '';
            categoryUpdate.subnet = calculateNetworkAddress2(ip, mask);
          }
        }

        if (
          key === 'ip' &&
          noIpInterfaces &&
          hasGlobalIpSubnet &&
          globalIpSubnet?.subnetMask
        ) {
          nextSubnetMask = value ? globalIpSubnet.subnetMask : '';
        }

        newNodes[index].data.category = {
          ...category,
          ...categoryUpdate,
          ...(noIpInterfaces && key === 'ip' && hasGlobalIpSubnet
            ? { subnetMask: nextSubnetMask }
            : {}),
        };
      }

      const allSubnets = newNodes
        .filter(
          (el) =>
            !(el.data.category as NodeCategoryModel).ipInterfaces ||
            !(el.data.category as NodeCategoryModel).ipInterfaces?.length,
        )
        .map((el) => (el.data.category as NodeCategoryModel).subnet);
      // check
      let message = '';
      const isValidSubnet = allElementsAreEqual(allSubnets);
      if (!isValidSubnet) {
        message = 'The ip addresses are not in the same subnet';
      }
      const allSetSubnet = allElementsValid(allSubnets);
      if (!allSetSubnet) {
        const unsetSubnet = newNodes
          .filter(
            (el) =>
              (!(el.data.category as NodeCategoryModel).ipInterfaces ||
                !(el.data.category as NodeCategoryModel).ipInterfaces
                  ?.length) &&
              !(el.data.category as NodeCategoryModel).subnet,
          )
          .map((el) => (el.data.category as NodeCategoryModel).name);
        message = `${unsetSubnet.join(',')} no subnet yet`;
      }

      setGlobalError(message);

      return newNodes;
    });
  };

  const onChangeInterface = (
    nodeId: string,
    index: number,
    key: string,
    value: string,
  ) => {
    setCurrentNodes((old) => {
      const newNodes = [...old];
      const nodeIndex = newNodes.findIndex((n) => n.id === nodeId);
      newNodes[nodeIndex].data.category = {
        ...(newNodes[nodeIndex].data.category as NodeCategoryModel),
        ipInterfaces: (
          newNodes[nodeIndex].data.category as NodeCategoryModel
        ).ipInterfaces?.map((ip, i) =>
          i === index ? { ...ip, [key]: value } : ip,
        ),
      };

      return newNodes;
    });
  };

  const onSave = () => {
    takeSnapshot();
    const otherNodes = nodes.filter(
      (n) =>
        (n.data?.category as NodeCategoryModel)?.type !== EDataType.Device &&
        (n.data?.category as NodeCategoryModel)?.type !== EDataType.Switch,
    );
    const unmanagedNodes = nodes.filter(
      (n) =>
        (n.data?.category as NodeCategoryModel)?.type === EDataType.Switch &&
        (n.data?.category as NodeCategoryModel)?.switchType?.includes(
          EDataSwitchType.UNMANAGED,
        ),
    );
    const newNodes = [...currentNodes, ...otherNodes, ...unmanagedNodes];
    setNodes(newNodes);
    dispatchNodesChanged(currentNodes.map((n) => n.id));
    setShowDialogs({ setting: false });
  };

  useEffect(() => {
    const cloned = JSON.parse(JSON.stringify(nodes)) as Node[];
    const { orderedNodes, remainingNodes } = orderNodes(
      cloned.filter(filterCanSettingNode),
      edges,
    );
    const grouped = groupBySwitch(orderedNodes);
    const sortedGroups = sortGroupsBySwitchName(grouped);
    sortedGroups.push([...remainingNodes]);

    const colorsMap = new Map<string, string>();
    sortedGroups.forEach((group, index) => {
      group.forEach((node) => {
        colorsMap.set(node.id, index % 2 === 0 ? 'bg-gray-100' : 'bg-white');
      });
    });
    setGroupColor(Object.fromEntries(colorsMap));
    setCurrentNodes(sortDevicesWithinGroups(sortedGroups));
    setTimeout(() => {
      setIsLoading(false);
    }, 200);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showDialogs.setting]);

  return (
    <Dialog
      open
      onClose={onCancel}
      className="relative z-50"
      data-testid="dialog-setting"
    >
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
      />

      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <DialogPanel
            transition
            className={twMerge([
              'relative transform overflow-hidden rounded-lg bg-white px-2 pb-2 pt-3 text-left shadow-xl transition-all sm:my-8 sm:p-6',
              'data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95',
            ])}
            data-testid="dialog-setting-panel"
          >
            <DialogTitle
              as="h3"
              className="pb-4 text-xl font-bold"
              data-testid="dialog-setting-title"
            >
              Device IP addresses
            </DialogTitle>
            <div className="absolute right-4 top-4">
              <button
                type="button"
                onClick={onCancel}
                data-testid="dialog-setting-close-button"
                className="text-gray-400 hover:text-gray-500"
              >
                <span className="sr-only">Close</span>
                <CloseIon className="h-6 w-6" />
              </button>
            </div>
            <div
              data-testid="dialog-setting-table"
              className="relative max-h-[70vh] overflow-x-auto"
            >
              {isLoading ? (
                <div className="flex h-full min-h-[200px] min-w-[500px] items-center justify-center">
                  <LoadingSpinner className="h-10 w-10 text-gray-500" />
                </div>
              ) : (
                <table className="min-w-full divide-x divide-y divide-gray-300 overflow-y-auto">
                  <thead>
                    <tr className="sticky top-0 z-50 divide-x divide-gray-300 bg-gray-200 shadow">
                      <th
                        scope="col"
                        className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-3"
                      >
                        Name
                      </th>
                      {useCustomVlan && (
                        <th
                          scope="col"
                          className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                        >
                          VLAN
                        </th>
                      )}
                      <th
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                      >
                        IP Address
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                      >
                        Subnet Mask
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                      >
                        Gateway
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white">
                    {currentNodes.map((node, nodeIndex) => {
                      const ipInterfaceLength = (
                        (node.data?.category as NodeCategoryModel)
                          ?.ipInterfaces || []
                      ).length;

                      const checkNodeValid = validateNode(
                        node.id,
                        node.data?.category as NodeCategoryModel,
                        nodes as Node<NodeModel>[],
                        edges as Edge[],
                        useCustomVlan ? '' : (globalIpSubnet?.ipSubnet ?? ''),
                        true,
                        useCustomVlan ? undefined : globalIpSubnet?.subnetMask,
                      );

                      (node.data?.category as NodeCategoryModel).isValid =
                        checkNodeValid.isValid;
                      (
                        node.data?.category as NodeCategoryModel
                      ).validationErrors = checkNodeValid.errorMessage;

                      const renderName = () => (
                        <FormInput
                          data-testid={`dialog-setting-name-input-${node.id}`}
                          id={`category-name-${node.id}`}
                          name={`category-name-${node.id}`}
                          placeholder={
                            (node.data?.category as NodeCategoryModel)?.type ===
                            EDataType.Switch
                              ? 'Switch'
                              : (node.data.label as string)
                          }
                          wrapperClassName="min-w-64"
                          error={checkNodeValid.errorMessage.name}
                          value={
                            (node.data?.category as NodeCategoryModel)?.name
                          }
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            onChangeData(
                              node.id as string,
                              'name',
                              e.target.value,
                            )
                          }
                        />
                      );

                      const isManagedSwitch = (
                        node.data?.category as NodeCategoryModel
                      )?.switchType?.includes(EDataSwitchType.MANAGED);

                      if (ipInterfaceLength > 0) {
                        return (
                          <Fragment key={nodeIndex}>
                            {(
                              node.data?.category as NodeCategoryModel
                            )?.ipInterfaces?.map(
                              (ipInterface, ipInterfaceIndex) => {
                                const disabledLength = countLeading255Octets(
                                  ipInterface.subnetMask,
                                );

                                const inputValue =
                                  disabledLength > 0 && !ipInterface.ip
                                    ? ipInterface.subnet
                                        .split('.')
                                        .slice(0, disabledLength)
                                        .join('.')
                                    : ipInterface.ip;

                                return (
                                  <tr
                                    key={ipInterfaceIndex}
                                    className={twMerge([
                                      'divide-x divide-y border-b',
                                      ipInterfaceIndex === ipInterfaceLength - 1
                                        ? 'border-gray-300'
                                        : 'border-gray-200',
                                      groupColor[node.id],
                                    ])}
                                  >
                                    {ipInterfaceIndex === 0 && (
                                      <td
                                        className={twMerge([
                                          'whitespace-nowrap border-r px-3 py-2 text-sm text-gray-900',
                                          (
                                            node.data
                                              ?.category as NodeCategoryModel
                                          )?.isValid
                                            ? ''
                                            : 'text-red-500',
                                        ])}
                                        rowSpan={ipInterfaceLength ?? 1}
                                      >
                                        {renderName()}
                                      </td>
                                    )}
                                    {useCustomVlan && (
                                      <td className="whitespace-nowrap px-3 py-2 text-center text-sm text-gray-900">
                                        <span className="text-gray-500">
                                          {ipInterface.vlanId}
                                        </span>
                                      </td>
                                    )}
                                    <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-900">
                                      <div className="flex flex-col gap-1">
                                        <IPInput
                                          key={ipInterfaceIndex}
                                          id={`category-ip-${node.id}-${ipInterfaceIndex}`}
                                          name={`category-ip-${node.id}-${ipInterfaceIndex}`}
                                          disabledLength={
                                            inputValue
                                              ? disabledLength
                                              : undefined
                                          }
                                          value={inputValue}
                                          data-testid={`dialog-setting-ip-input-${node.id}-${ipInterfaceIndex}`}
                                          error={
                                            checkNodeValid.errorMessage
                                              .ipInterfaces[ipInterfaceIndex].ip
                                          }
                                          onChange={(value) => {
                                            let newValue = '';
                                            const valueParts = value
                                              .split('.')
                                              .filter((part) => part !== '');
                                            if (
                                              valueParts.length > disabledLength
                                            ) {
                                              newValue = value;
                                            }
                                            onChangeInterface(
                                              node.id as string,
                                              ipInterfaceIndex,
                                              'ip',
                                              newValue,
                                            );
                                          }}
                                          placeholder="192.168.1.0"
                                          wrapperClassName="w-auto"
                                          dataTestId={`category-ip-${node.id}-${ipInterfaceIndex}`}
                                        />
                                      </div>
                                    </td>
                                    <td className="relative whitespace-nowrap py-2 pl-3 pr-4 text-right text-sm font-medium sm:pr-3">
                                      <div className="flex flex-col gap-1">
                                        <IPInput
                                          key={ipInterfaceIndex}
                                          id={`category-subnet-mask-${node.id}-${ipInterfaceIndex}`}
                                          name={`category-subnet-mask-${node.id}-${ipInterfaceIndex}`}
                                          value={ipInterface.subnetMask}
                                          disabled
                                          data-testid={`dialog-setting-subnet-mask-input-${node.id}-${ipInterfaceIndex}`}
                                          error={
                                            checkNodeValid.errorMessage
                                              .ipInterfaces[ipInterfaceIndex]
                                              .subnetMask
                                          }
                                          onChange={(value) =>
                                            onChangeInterface(
                                              node.id as string,
                                              ipInterfaceIndex,
                                              'subnetMask',
                                              value,
                                            )
                                          }
                                          placeholder="255.255.255.0"
                                          wrapperClassName="w-auto"
                                          dataTestId={`category-subnet-mask-${node.id}-${ipInterfaceIndex}`}
                                        />
                                      </div>
                                    </td>
                                    {ipInterfaceIndex === 0 && (
                                      <td
                                        className="whitespace-nowrap px-3 py-2 text-sm text-gray-900"
                                        rowSpan={ipInterfaceLength ?? 1}
                                      >
                                        {isManagedSwitch && (
                                          <IPInput
                                            id={`category-gateway-multi-${node.id}`}
                                            name={`category-gateway-multi-${node.id}`}
                                            value={
                                              (
                                                node.data
                                                  ?.category as NodeCategoryModel
                                              )?.gateway || ''
                                            }
                                            data-testid={`dialog-setting-gateway-input-multi-${node.id}`}
                                            error={
                                              checkNodeValid.errorMessage
                                                .gateway
                                            }
                                            onChange={(value) =>
                                              onChangeData(
                                                node.id as string,
                                                'gateway',
                                                value,
                                              )
                                            }
                                            placeholder="192.168.1.1"
                                            wrapperClassName="w-auto"
                                            dataTestId={`category-gateway-multi-${node.id}`}
                                          />
                                        )}
                                      </td>
                                    )}
                                  </tr>
                                );
                              },
                            )}
                          </Fragment>
                        );
                      }

                      return (
                        <tr
                          key={nodeIndex}
                          className={twMerge([
                            'divide-x divide-y border-b border-gray-300',
                            groupColor[node.id],
                          ])}
                        >
                          <td
                            className={twMerge([
                              'whitespace-nowrap px-3 py-2 text-sm text-gray-900',
                              (node.data?.category as NodeCategoryModel)
                                ?.isValid
                                ? ''
                                : 'text-red-500',
                            ])}
                          >
                            {renderName()}
                          </td>
                          {useCustomVlan && (
                            <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-900" />
                          )}
                          <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-900">
                            <IPInput
                              id={`category-ip-${node.id}`}
                              name="nodeIp"
                              data-testid={`dialog-setting-ip-input-${node.id}`}
                              disabledLength={
                                hasGlobalIpSubnet && globalIpSubnet?.subnetMask
                                  ? countLeading255Octets(
                                      globalIpSubnet.subnetMask,
                                    )
                                  : undefined
                              }
                              value={(() => {
                                const cat = node.data
                                  ?.category as NodeCategoryModel;
                                const ip = cat?.ip || '';
                                if (
                                  hasGlobalIpSubnet &&
                                  globalIpSubnet?.subnetMask &&
                                  globalIpSubnet?.ipSubnet
                                ) {
                                  const disabledLen = countLeading255Octets(
                                    globalIpSubnet.subnetMask,
                                  );
                                  if (disabledLen > 0 && !ip)
                                    return globalIpSubnet.ipSubnet
                                      .split('.')
                                      .slice(0, disabledLen)
                                      .join('.');
                                }
                                return ip;
                              })()}
                              error={checkNodeValid.errorMessage.ip}
                              placeholder="192.168.1.0"
                              wrapperClassName="w-auto"
                              onChange={(value) => {
                                if (
                                  hasGlobalIpSubnet &&
                                  globalIpSubnet?.subnetMask
                                ) {
                                  const disabledLength = countLeading255Octets(
                                    globalIpSubnet.subnetMask,
                                  );
                                  const valueParts = value
                                    .split('.')
                                    .filter((part) => part !== '');
                                  const newValue =
                                    valueParts.length > disabledLength
                                      ? value
                                      : '';
                                  onChangeData(
                                    node.id as string,
                                    'ip',
                                    newValue,
                                  );
                                } else {
                                  onChangeData(node.id as string, 'ip', value);
                                }
                              }}
                              dataTestId={`category-ip-${node.id}`}
                            />
                          </td>
                          <td className="relative whitespace-nowrap py-2 pl-3 pr-4 text-right text-sm font-medium sm:pr-3">
                            <IPInput
                              id={`category-subnet-mask-${node.id}`}
                              name="nodeSubNetmask"
                              data-testid={`dialog-setting-subnet-mask-input-${node.id}`}
                              value={
                                (node.data?.category as NodeCategoryModel)
                                  ?.subnetMask ||
                                (!useCustomVlan && globalIpSubnet?.subnetMask
                                  ? globalIpSubnet.subnetMask
                                  : '')
                              }
                              placeholder="255.255.255.0"
                              error={checkNodeValid.errorMessage.subnetMask}
                              disabled={
                                !!(!useCustomVlan && globalIpSubnet?.subnetMask)
                              }
                              onChange={(value) =>
                                onChangeData(
                                  node.id as string,
                                  'subnetMask',
                                  value,
                                )
                              }
                              dataTestId={`category-subnet-mask-${node.id}`}
                            />
                          </td>
                          <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-900">
                            {isManagedSwitch && (
                              <IPInput
                                id={`category-gateway-single-${node.id}`}
                                name="nodeGateway"
                                data-testid={`dialog-setting-gateway-input-single-${node.id}`}
                                value={
                                  (node.data?.category as NodeCategoryModel)
                                    ?.gateway || ''
                                }
                                placeholder="192.168.1.1"
                                error={checkNodeValid.errorMessage.gateway}
                                onChange={(value) =>
                                  onChangeData(
                                    node.id as string,
                                    'gateway',
                                    value,
                                  )
                                }
                                dataTestId={`category-gateway-single-${node.id}`}
                              />
                            )}
                          </td>
                        </tr>
                      );
                    })}
                    {currentNodes.length === 0 && (
                      <tr>
                        <td
                          colSpan={5}
                          className="px-4 py-6 text-sm text-gray-700"
                          data-testid="dialog-setting-no-data-row"
                        >
                          No data
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              )}
            </div>
            <div className="mt-4 flex justify-between">
              <div>
                {globalError && (
                  <p
                    className="text-left text-sm text-red-500"
                    data-testid="dialog-setting-global-error"
                  >
                    {globalError}
                  </p>
                )}
              </div>
              <div className="flex items-center justify-end gap-2">
                <Button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    onSave();
                  }}
                  // disabled={!allNodeValid || !!globalError}
                  data-testid="dialog-setting-save-button"
                >
                  Save
                </Button>
              </div>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
}
